import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";
import { SavedCommodityCreatedSourceEnum } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { SAVED_COMMODITY_FILTER_ANY } from "../Hooks/useSavedCommoditiesFilters";
import type { SavedCommodityProblem } from "../Helpers/savedCommoditiesProblems";

const sourceOptions = [
  { value: SAVED_COMMODITY_FILTER_ANY, label: "Any source" },
  { value: SavedCommodityCreatedSourceEnum.ShopifySync, label: "Shopify sync" },
  { value: SavedCommodityCreatedSourceEnum.Dashboard, label: "Dashboard" },
  {
    value: SavedCommodityCreatedSourceEnum.BulkUpload,
    label: "Spreadsheet upload",
  },
  { value: SavedCommodityCreatedSourceEnum.PublicApi, label: "API" },
];

interface SavedCommoditiesFilterBarProps {
  source: string;
  setSource: (value: string) => void;
  problem: string;
  setProblem: (value: string) => void;
  counts: Record<SavedCommodityProblem, number>;
}

/**
 * The two filters that answer "which of these can't be quoted, and who made them".
 *
 * A Shopify sync can produce thousands of commodities, and the ones missing dimensions or a SKU are
 * the ones that silently never quote. Finding them used to mean us running a query and emailing a
 * list (ENG-6665).
 *
 * The problem options carry their counts, because the number is the useful part: "Missing dimensions
 * (63)" tells a merchant whether to care before they click, and a filter that turns out to be empty
 * has wasted the click.
 */
export function SavedCommoditiesFilterBar({
  source,
  setSource,
  problem,
  setProblem,
  counts,
}: SavedCommoditiesFilterBarProps) {
  const problemOptions = [
    { value: SAVED_COMMODITY_FILTER_ANY, label: "Any" },
    {
      value: "missingDimensions",
      label: `Missing dimensions (${counts.missingDimensions})`,
    },
    { value: "missingSku", label: `Missing SKU (${counts.missingSku})` },
  ];

  return (
    // Labels beside the controls rather than above: this is a filter bar, not a form. Same reasoning
    // as the shipments filter bar, and the same boxed presentation so the pair reads as controls.
    <ControlPresentationProvider presentation="boxed">
      <Row gap={4} align="center" wrap>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Source
          </T>
          <Dropdown
            options={sourceOptions}
            value={source}
            onChange={(value) => setSource(value ?? SAVED_COMMODITY_FILTER_ANY)}
            width="md"
            data-testid="saved-commodities-source-filter"
          />
        </Row>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Problem
          </T>
          <Dropdown
            options={problemOptions}
            value={problem}
            onChange={(value) =>
              setProblem(value ?? SAVED_COMMODITY_FILTER_ANY)
            }
            width="md"
            data-testid="saved-commodities-problem-filter"
          />
        </Row>
      </Row>
    </ControlPresentationProvider>
  );
}
